/**
 * FlagsChartTab — Flags inner tab of the patient Chart shell.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/flags_tab.dart`.
 *
 * Flags are clinical / social / behavioural banners attached to the
 * patient so they surface in encounters (e.g. "Wandering risk",
 * "Hearing impaired", "VIP donor"). Distinct from the calendar
 * row-warning flags shipped under the scheduling module.
 *
 * **Add / Delete (PROD-827)**: shipped 2026-05-20.
 * - **Add** opens `AddFlagsDialog` — a multi-select modal showing the
 *   master flag catalog grouped by category. Selecting flags + Done
 *   POSTs the whole patient flag state via `/CreateFlag` (array
 *   body, whole-state replacement semantic).
 * - **Delete** soft-deletes a single row via the same endpoint —
 *   build a body where the target row has `isDeleted: true` and ship
 *   the full state alongside. No separate `/delete/{id}` endpoint
 *   exists on this resource.
 *
 * There is no separate "Edit" surface — flags don't have editable
 * fields beyond selection. Adjusting a flag's start/end dates is
 * out of scope for V1 (Flutter shows them but the chart Add dialog
 * doesn't expose date editing either).
 *
 * **Expired computation** — compares `endDate` as a YYYY-MM-DD string
 * against today's local date. This sidesteps the parser-vs-timezone
 * surprise where `new Date("2026-01-15T00:00:00")` (no `Z` suffix —
 * standard Scrips wire format) is parsed as LOCAL midnight, which
 * would shift the active/expired boundary by ±4 hours when a Dubai
 * record is viewed from a non-Dubai timezone. Date-only comparison
 * is the safe answer for "is this past today" semantics — Samer PR
 * #165 review 2026-05-15.
 *
 * **Pagination** — the wire returns the full list (no server pager).
 * Real records carry hundreds of flags (462 observed on a stress
 * patient). The table renders one page of 25 at a time with a
 * Previous/Next pager — same shape as Appointments + Receipts.
 */

import { useEffect, useState } from 'react';
import { StatusChip, ModalYesNo } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientFlagsChart } from '@/patients/services/use-patient-flags-chart';
import { useCreateFlag } from '@/patients/services/use-create-flag';
import { ChartSectionHeader } from './chart-section-header';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { ChartRowIconActions } from '@/shared/ui/components/chart-row-actions';
import { AddFlagsDialog } from './add-flags-dialog';
import type { CreateFlagBody, PatientFlag } from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { CHART_TD_V2, CHART_TH_V2, chartCell, chartTableCard, type ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

const PAGE_SIZE = 25;

export interface FlagsChartTabProps {
  patientId: string | null | undefined;
}

export function FlagsChartTab({ patientId }: FlagsChartTabProps) {
  const { data, isLoading, isError, refetch } = usePatientFlagsChart(patientId);
  const allRows = data ?? [];
  // Visible-list filter — strict `!== true` (Samer PR #157 pattern).
  // ALSO requires `categoryName != null` (LOOSE check — Samer PR #250
  // review §4) to filter out ghost placeholder rows from the GET
  // (master flags the patient never selected) which carry
  // isDeleted:false + categoryName:null + createdBy:null.
  //
  // **Why loose `!= null` and NOT strict `!== null && !== ""`**:
  // A real selected flag's top-level `categoryName` is the category
  // bucket (e.g. "CHART", "COMMUNICATION") — never an empty string on
  // active records. Ghost placeholders use `null`, not `""`. The
  // empty-string `""` on `flagResponse.categoryName` (curl-verified
  // sub-field, not this top-level field) is a SEPARATE concern that
  // shouldn't hide rows here. If a real patient flag ever came back
  // with `categoryName: ""`, we WANT it visible — preserving the
  // loose check is intentional.
  const rows = allRows.filter(
    (f) => f.isDeleted !== true && f.categoryName != null,
  );
  const createMut = useCreateFlag();

  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState<PatientFlag | null>(null);

  // Reset to page 1 if the patient changes (or the underlying list shrinks
  // below the current page boundary).
  useEffect(() => {
    setPage(1);
  }, [patientId, rows.length]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const visible = rows.slice(start, start + PAGE_SIZE);
  // The first 5 flags of the page, "Show all N" for the rest of it; the pager waits behind it. The index counts every flag.
  const cap = useRowCap(visible);
  useReportChartCount(isLoading ? null : rows.length);

  // Soft-delete via POST /CreateFlag with the full state, target row
  // marked isDeleted:true. Strict createdBy guard — refuse to ship if
  // the audit trail is missing (mirrors Allergies/Conditions/Procedures
  // pattern).
  //
  // **UX-visible failure mode** (Samer PR #250 review §2):
  // when the audit guard fires we surface a `toast.error(...)` via
  // sonner (project's global server-error transport — see
  // `src/shared/api/api-client.ts`) and `return` without calling the
  // mutation. The AlertDialog stays OPEN; the user sees the toast
  // banner — NOT a silent no-op and NOT a console-only warning. Same
  // wording + same path as the editor-side audit guards on Allergies
  // (`add-allergy-dialog.tsx`), Procedures (`add-procedure-editor.tsx`)
  // and Social/Family History (`add-social-history-editor.tsx`,
  // `add-family-history-editor.tsx`).
  const confirmDelete = async () => {
    if (!deleting?.identifier || !patientId) return;
    if (!deleting.createdBy) {
      toast.error(
        'Cannot delete — this record is missing its original recorder. Please contact support.',
      );
      return;
    }
    const targetId = deleting.identifier;
    const body: CreateFlagBody = allRows.map((r) => {
      if (r.identifier === targetId) {
        return { ...r, isDeleted: true };
      }
      return r;
    });
    try {
      await createMut.mutateAsync({ patientId, body });
      toast.success(
        `Removed flag: ${deleting.flagResponse?.display ?? 'Unknown'}`,
      );
      setDeleting(null);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to record flags."
          />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load flags."
            ctaLabel="Retry"
            onCtaClick={() => void refetch()}
          />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-3">
      <ChartSectionHeader
        title="Flags"
        count={rows.length}
        onAdd={() => setAddOpen(true)}
      />

      {rows.length === 0 ? (
        <EmptyState
            icon="flag"
            title="No flags recorded for this patient."
            ctaLabel="Add flag"
            onCtaClick={() => setAddOpen(true)}
          />
      ) : (
        <>
          <div className={chartTableCard('rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
            <table className="w-full text-sm">
              <thead className="bg-grey-100">
                <tr className="text-left text-text-muted">
                  <Th>#</Th>
                  <Th>Flag</Th>
                  <Th>Category</Th>
                  <Th>Status</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th>Recorder</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {cap.visible.map((f, idx) => {
                  const i = start + idx;
                  const expired = isExpired(f.endDate);
                  return (
                    <tr
                      key={
                        f.identifier ??
                        `${i}-${f.flagResponse?.code ?? 'unknown'}`
                      }
                      className="border-t border-border-default hover:bg-grey-100/50"
                    >
                      <Td cell="index">{i + 1}</Td>
                      <Td cell="name">
                        <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                          {f.flagResponse?.display ?? '—'}
                        </span>
                      </Td>
                      <Td cell="text">
                        {/* Category precedence: top-level `categoryName`
                            wins over the nested `flagResponse.categoryName`.
                            Flutter ships both; they should match but if
                            they diverge the top-level is the authoritative
                            one. */}
                        {f.categoryName ?? f.flagResponse?.categoryName ?? '—'}
                      </Td>
                      <Td cell="short">
                        <StatusChip
                          tone="soft"
                          semantic={expired ? 'neutral' : 'info'}
                        >
                          {expired ? 'Expired' : 'Active'}
                        </StatusChip>
                      </Td>
                      <Td date>{formatDate(f.startDate)}</Td>
                      <Td date>{formatDate(f.endDate)}</Td>
                      <Td cell="text">
                        {f.performerDetails?.name ?? f.createdBy ?? '—'}
                      </Td>
                      <Td align="right" cell="actions">
                        <ChartRowIconActions
                          editLabel={`Edit flag: ${f.flagResponse?.display ?? 'Unknown'}`}
                          deleteLabel={`Delete flag: ${f.flagResponse?.display ?? 'Unknown'}`}
                          // No row-level Edit — the AddFlagsDialog
                          // is the only editing surface (selection
                          // is the only editable field). Open the
                          // dialog with current state.
                          onEdit={() => setAddOpen(true)}
                          onDelete={() => setDeleting(f)}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <ChartShowAll cap={cap} label="flags" />

          {!cap.capped || cap.expanded ? (
          <div className="flex items-center justify-between text-sm text-text-muted">
            <span>
              {`Showing ${start + 1}–${Math.min(start + PAGE_SIZE, rows.length)} of ${rows.length}`}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-text-hint">
                Page {safePage} of {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={safePage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
          ) : null}
        </>
      )}

      <AddFlagsDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        patientId={patientId}
        currentFlags={allRows}
      />

      <ModalYesNo
      open={deleting !== null}
      onCancel={() => setAddOpen(false)}
      title="Are you sure?"
      message={<>Do you want to proceed on deleting{' '}
              <span className={v2Look('font-semibold text-brand-depth', V2_BODY_EMPHASIS)}>
                {deleting?.flagResponse?.display ?? 'this flag'}
              </span>
              ?</>}
      cancelLabel="Cancel"
      confirmLabel={createMut.isPending ? 'Deleting…' : 'Delete'}
      onConfirm={() => {
                void confirmDelete();
              }}
      loading={createMut.isPending}
    />
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={chartCell(
        `px-3 py-2 text-xs font-bold uppercase tracking-wide ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TH_V2,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note, actions). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
  /** A date: kept on one line (the bridge's "patient chart" block, `data-chart-cell="date"`). */
  date?: boolean;
}) {
  return (
    <td
      data-chart-cell={date ? 'date' : cell}
      data-tip={tip}
      className={chartCell(
        `px-3 py-2 align-top ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TD_V2,
      )}
    >
      {children}
    </td>
  );
}

/**
 * True when `endIso` represents a date strictly before today's local
 * date. Uses date-only string comparison (YYYY-MM-DD) so the active /
 * expired boundary doesn't drift by a few hours when the user's
 * timezone differs from the Scrips wire format's implicit local
 * timezone. Verified against Samer PR #165 review 2026-05-15.
 */
function isExpired(endIso: string | null | undefined): boolean {
  if (!endIso) return false;
  // Strip the time portion entirely. The wire ships either
  // `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm:ss(.sss)` (no Z) — both safe
  // to truncate at index 10.
  const endDate = endIso.slice(0, 10);
  if (endDate.length < 10) return false;
  const today = localTodayIso();
  return endDate < today;
}

function localTodayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
